import { Flex } from '@utilitywarehouse/hearth-react';
import { Formik } from 'formik';
import { FC, MouseEvent, useEffect, useRef, useState } from 'react';

import { DialogID, useDialog } from '@components/Dialog';
import { FormikPersist } from '@components/FormikPersist';
import { NavBar } from '@components/NavBar';
import { ScrollToError } from '@components/ScrollToError';
import { UpdatingModal } from '@components/UpdatingModal';
import { BOILER_HOME_COVER } from '@constants/formNames';
import { BHCFAQ } from '@features/BoilerHomeCover/BHCFAQ';
import { BHCHero } from '@features/BoilerHomeCover/BHCHero';
import { FormSync } from '@features/Remote/Interaction/FormSync';
import { RemoveDialog } from '@features/RemoveDialog';
import { ProductSubtype, ProductType } from '@generated/gql/graphql';
import { BoilerInsuranceQuote } from '@src/types/insurance';
import { getQuoteMonthlyCharge, getQuoteMonthlyPrice } from '@utils/price';

import { BHCLegal } from './BHCLegal';
import { Covered } from './Covered';
import { useBoilerHomeCoverHandler } from './useBoilerHomeCoverHandler';
import { validationSchema } from './validationSchema';
import { BHCLegalSectionValues } from './validationSchema';

const initialValues: BHCLegalSectionValues = {
  hasAcceptedInformation: false,
};

interface BoilerHomeCoverProps {
  basketId: string;
  insuranceQuote: BoilerInsuranceQuote;
  onSelectCompleted?: () => void;
  onDeselectCompleted?: () => void;
}

const BoilerHomeCover: FC<BoilerHomeCoverProps> = ({
  basketId,
  insuranceQuote,
  onSelectCompleted,
  onDeselectCompleted,
}) => {
  const formRef = useRef<HTMLFormElement>(null);

  const updatingDialog = useDialog(DialogID.UPDATING_BUNDLE);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

  const { onSubmit, isLoading, isRemoving, removeInsurance } =
    useBoilerHomeCoverHandler(
      insuranceQuote,
      basketId,
      onSelectCompleted,
      onDeselectCompleted
    );

  const addToBundle = (event: MouseEvent) => {
    event.preventDefault();
    formRef?.current?.requestSubmit();
    return;
  };

  useEffect(() => {
    updatingDialog.setOpen(isLoading);
  }, [isLoading, updatingDialog]);

  return (
    <>
      <div className="wrapper">
        <Flex direction="column" spacing="2xl" paddingBottom="400">
          <BHCHero
            monthlyCharge={getQuoteMonthlyCharge(insuranceQuote)?.amount}
            price={getQuoteMonthlyPrice(insuranceQuote)}
            discounts={insuranceQuote.discounts}
          />
          <Covered />
          <Formik
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={onSubmit}
          >
            {({ handleSubmit }) => (
              <>
                <FormSync />
                <FormikPersist name={BOILER_HOME_COVER} />
                <form ref={formRef} onSubmit={handleSubmit}>
                  <BHCLegal
                    documents={insuranceQuote.product.details.documents}
                  />
                  <ScrollToError orderedFields={['hasAcceptedInformation']} />
                </form>
              </>
            )}
          </Formik>
        </Flex>
      </div>
      <BHCFAQ />
      <RemoveDialog
        isOpen={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
        onRemove={removeInsurance}
        productType={ProductType.Insurance}
        productName={insuranceQuote.product.name}
      />
      <NavBar
        nextLabel={insuranceQuote.selected ? 'Save' : 'Add to bundle'}
        onNext={addToBundle}
        nextDisabled={isLoading}
        hasRemoveButton={insuranceQuote.selected}
        onRemove={() => setRemoveDialogOpen(true)}
      />
      <UpdatingModal
        heading="Updating your bundle..."
        productType={ProductType.Insurance}
        subType={ProductSubtype.BoilerHomeCover}
        isRemoving={isRemoving}
      />
    </>
  );
};

export { BoilerHomeCover };
